import { useState, useEffect } from 'react'

const LOGO_STORAGE_KEY = 'app_logo'
const DEFAULT_LOGO = 'logo1.png'

// Check if logo is a predefined logo (starts with logo) or uploaded URL
const isPredefinedLogo = (logoValue: string): boolean => {
  return logoValue.startsWith('logo') && !logoValue.startsWith('http')
}

export const useLogo = () => {
  const [logo, setLogo] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(LOGO_STORAGE_KEY) || DEFAULT_LOGO
    }
    return DEFAULT_LOGO
  })

  // Listen for storage changes to sync across tabs and same-tab updates
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === LOGO_STORAGE_KEY && e.newValue) {
        setLogo(e.newValue)
      }
    }

    const handleLogoChange = (e: CustomEvent) => {
      if (e.detail?.logo) {
        setLogo(e.detail.logo)
      }
    }

    // Listen for cross-tab storage events
    window.addEventListener('storage', handleStorageChange)
    
    // Listen for same-tab custom events
    window.addEventListener('logoChanged', handleLogoChange as EventListener)
    
    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('logoChanged', handleLogoChange as EventListener)
    }
  }, [])

  const switchLogo = (logoName: string) => {
    // Accept both predefined logos and uploaded URLs
    localStorage.setItem(LOGO_STORAGE_KEY, logoName)
    setLogo(logoName)
    // Dispatch custom event for same-tab updates
    window.dispatchEvent(new CustomEvent('logoChanged', {
      detail: { logo: logoName }
    }))
  }

  // Get logo URL - handle both predefined and uploaded logos
  const getLogoUrl = (): string => {
    if (isPredefinedLogo(logo)) {
      return `/assets/${logo}`
    }
    // It's an uploaded logo URL (from Supabase Storage)
    return logo
  }

  return {
    logo: getLogoUrl(),
    switchLogo,
    currentLogo: logo,
  }
}
